import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/infrastructure/api/client";
import {
  ApiError,
  type PaymentConfirmationDto,
  type CreatePaymentDto,
  type SettlementWithPaymentDto,
} from "@tabipla/shared";
import { paymentKeys } from "./queryKeys";
import { invalidatePaymentQueries } from "./invalidation";

// Fetch payments for a session
export function usePayments(groupId: string, sessionId: string) {
  return useQuery({
    queryKey: paymentKeys.session(groupId, sessionId),
    queryFn: async () => {
      const response = await apiClient.get<PaymentConfirmationDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch payments");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

// Fetch settlements with payment status
export function useSettlementsWithPayments(groupId: string, sessionId: string) {
  return useQuery({
    queryKey: paymentKeys.settlementsWithPayments(groupId, sessionId),
    queryFn: async () => {
      const response = await apiClient.get<SettlementWithPaymentDto[]>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments/settlements`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch settlements");
      }
      return response.data;
    },
    enabled: !!groupId && !!sessionId,
  });
}

// Create a payment record
export function useCreatePayment(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePaymentDto) => {
      const response = await apiClient.post<PaymentConfirmationDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create payment");
      }
      return response.data;
    },
    onSuccess: () => {
      invalidatePaymentQueries(queryClient, groupId, sessionId);
    },
  });
}

// Mark payment as paid (payer action)
// Uses optimistic updates for better UX.
export function useMarkPaymentPaid(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => {
      const response = await apiClient.post<PaymentConfirmationDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments/${paymentId}/paid`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to mark as paid");
      }
      return response.data;
    },
    onMutate: async (paymentId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: paymentKeys.settlementsWithPayments(groupId, sessionId),
      });

      // Snapshot previous value
      const previousSettlements = queryClient.getQueryData<SettlementWithPaymentDto[]>(
        paymentKeys.settlementsWithPayments(groupId, sessionId)
      );

      // Optimistically update the payment status
      if (previousSettlements) {
        queryClient.setQueryData<SettlementWithPaymentDto[]>(
          paymentKeys.settlementsWithPayments(groupId, sessionId),
          previousSettlements.map((s) =>
            s.paymentId === paymentId
              ? { ...s, paymentStatus: "paid" as const, paidAt: new Date().toISOString() }
              : s
          )
        );
      }

      return { previousSettlements };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousSettlements) {
        queryClient.setQueryData(
          paymentKeys.settlementsWithPayments(groupId, sessionId),
          context.previousSettlements
        );
      }
    },
    onSettled: () => {
      invalidatePaymentQueries(queryClient, groupId, sessionId);
    },
  });
}

// Confirm payment received (receiver action)
// Uses optimistic updates for better UX.
export function useConfirmPayment(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => {
      const response = await apiClient.post<PaymentConfirmationDto>(
        `/api/groups/${groupId}/sessions/${sessionId}/payments/${paymentId}/confirm`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to confirm payment");
      }
      return response.data;
    },
    onMutate: async (paymentId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: paymentKeys.settlementsWithPayments(groupId, sessionId),
      });

      // Snapshot previous value
      const previousSettlements = queryClient.getQueryData<SettlementWithPaymentDto[]>(
        paymentKeys.settlementsWithPayments(groupId, sessionId)
      );

      // Optimistically update the payment status
      if (previousSettlements) {
        queryClient.setQueryData<SettlementWithPaymentDto[]>(
          paymentKeys.settlementsWithPayments(groupId, sessionId),
          previousSettlements.map((s) =>
            s.paymentId === paymentId
              ? { ...s, paymentStatus: "confirmed" as const, confirmedAt: new Date().toISOString() }
              : s
          )
        );
      }

      return { previousSettlements };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousSettlements) {
        queryClient.setQueryData(
          paymentKeys.settlementsWithPayments(groupId, sessionId),
          context.previousSettlements
        );
      }
    },
    onSettled: () => {
      invalidatePaymentQueries(queryClient, groupId, sessionId);
    },
  });
}

// Delete a payment record
export function useDeletePayment(groupId: string, sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => {
      await apiClient.delete(`/api/groups/${groupId}/sessions/${sessionId}/payments/${paymentId}`);
    },
    onSuccess: () => {
      invalidatePaymentQueries(queryClient, groupId, sessionId);
    },
  });
}
